/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react'
import { SnackbarKey } from 'notistack'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { login } from '@/features/auth/services/authService'
import {
  enterRestaurant,
  pickRestaurant
} from '@/features/auth/services/currentRestaurant'
import { useTranslation } from 'react-i18next'
import { HTTPInstance } from '@/lib/http/http-instance'
import { z } from 'zod'
import { LoginErrorFormEffect } from './LoginErrorFormEffect'

type Restaurant = { id: string | number; [k: string]: any }

/*
  Firebase's answers to a wrong email or password. Anything else (no network,
  the API not up in time, too many attempts) is not in what was typed, so the
  fields are not marked and the owner is asked to try again instead.
*/
const BAD_CREDENTIALS = new Set([
  'auth/invalid-credential',
  'auth/invalid-email',
  'auth/user-not-found',
  'auth/wrong-password'
])

// A cold start answers in about 20 s. Past a minute something else is wrong,
// and the owner gets the button back rather than a banner that never goes.
const RESTAURANTS_TIMEOUT_MS = 60_000

export const useLoginFormConfig = () => {
  const { showSnackbar, closeSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [failureCount, setFailureCount] = useState(0)
  // Stays on after a success: the page is about to be replaced by the app.
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  // Not carried to another page, such as the password reset, if the owner
  // leaves while waiting.
  const pending = useRef<SnackbarKey | null>(null)
  useEffect(
    () => () => {
      if (pending.current !== null) closeSnackbar(pending.current)
    },
    [closeSnackbar]
  )

  const handleLogin = async (email: string, password: string) => {
    setIsLoggingIn(true)
    // Up for the whole wait, which a sleeping API stretches past the usual
    // five seconds. A success replaces the page, and the banner with it.
    pending.current = showSnackbar(
      t('ALERTS.LOGIN_PENDING.TITLE'),
      t('ALERTS.LOGIN_PENDING.DESCRIPTION'),
      'info',
      { persist: true }
    )
    try {
      await login(email, password)

      const { data } = await HTTPInstance.get<Restaurant[]>('/restaurants', {
        timeout: RESTAURANTS_TIMEOUT_MS
      })
      const restaurants = data ?? []

      const restaurant = pickRestaurant(restaurants)
      if (restaurant) {
        enterRestaurant(restaurant, '/table-cards-management')
      } else {
        throw new Error('No restaurants found for this user.')
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      if (pending.current !== null) closeSnackbar(pending.current)
      pending.current = null
      const isBadCredentials = BAD_CREDENTIALS.has(error?.code)
      showSnackbar(
        t('ALERTS.LOGIN_ERROR.TITLE'),
        t(
          isBadCredentials
            ? 'ALERTS.LOGIN_ERROR.DESCRIPTION'
            : 'ALERTS.LOGIN_ERROR.RETRY'
        ),
        'error'
      )
      if (isBadCredentials) setFailureCount(count => count + 1)
      setIsLoggingIn(false)
    }
  }

  const formConfig = {
    id: 'login-form',
    fields: [
      {
        name: 'email',
        label: t('AUTHORIZATION.LOGIN.EMAIL.LABEL'),
        placeholder: t('AUTHORIZATION.LOGIN.EMAIL.PLACEHOLDER'),
        focused: true,
        type: 'text',
        required: true,
        validation: z
          .string({
            required_error: t('AUTHORIZATION.LOGIN.EMAIL.VALIDATION.REQUIRED')
          })
          .trim()
          .email({
            message: t('AUTHORIZATION.LOGIN.EMAIL.VALIDATION.INVALID')
          })
      },
      {
        name: 'password',
        label: t('AUTHORIZATION.LOGIN.PASSWORD.LABEL'),
        placeholder: t('AUTHORIZATION.LOGIN.PASSWORD.PLACEHOLDER'),
        focused: true,
        type: 'password',
        required: true,
        validation: z
          .string({
            required_error: t(
              'AUTHORIZATION.LOGIN.PASSWORD.VALIDATION.REQUIRED'
            )
          })
          .trim()
          .min(8, {
            message: t('AUTHORIZATION.LOGIN.PASSWORD.VALIDATION.TOO_SHORT')
          })
      }
    ],
    onSubmit: async (data: Record<string, any>) => {
      await handleLogin(data.email, data.password)
    },
    FormEffects: <LoginErrorFormEffect failureCount={failureCount} />
  }

  return { formConfig, isLoggingIn }
}
